// O tipo e o tamanho dos arquivos da Central do Perito (fase C) — PURO, com prova própria
// (`tests/perito-despacho.mjs`). Portado de `src/lib/dados-mapa.ts` de lá (`tipoDoArquivo`), no commit f5c1923.
//
// Duas listas, e elas não são a mesma de propósito:
//   * os DOCUMENTOS da perícia aceitam os doze tipos do bucket `perito-documentos` (000385), até 20 MB;
//   * a LEITURA do despacho aceita só PDF e imagem, até 8 MB — é o que o modelo lê (`TIPOS_ACEITOS` e
//     `LIMITE_BYTES` em `supabase/functions/_shared/leitura-do-despacho.ts`).
// A tela confere ANTES de subir: o bucket e a função recusariam do mesmo jeito, mas com uma frase que não diz
// ao aluno o que fazer.

const TIPO_POR_EXTENSAO: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt: "text/plain",
  csv: "text/csv",
};

/** Os doze tipos que o bucket aceita (conferidos contra `storage.buckets` em 03/11). */
export const TIPOS_DE_DOCUMENTO = new Set(Object.values(TIPO_POR_EXTENSAO));
/** O limite do bucket. */
export const LIMITE_DOCUMENTO_BYTES = 20 * 1024 * 1024;

/** O que a leitura automática lê: PDF e imagem. */
export const TIPOS_DA_LEITURA = new Set(["application/pdf", "image/png", "image/jpeg", "image/webp", "image/heic", "image/heif"]);
/** O limite da leitura, o mesmo da função. */
export const LIMITE_DA_LEITURA_BYTES = 8 * 1024 * 1024;

/**
 * O tipo a gravar. Alguns navegadores entregam `file.type` vazio para .docx ou .heic; aí a extensão decide. Sem
 * tipo reconhecível, `null`, e a tela explica em vez de mandar para o bucket, que recusaria.
 */
export function tipoDoArquivo(nome: string, tipoInformado: string): string | null {
  const informado = tipoInformado.toLowerCase();
  if (TIPOS_DE_DOCUMENTO.has(informado)) return informado;
  const extensao = nome.toLowerCase().split(".").pop() ?? "";
  return TIPO_POR_EXTENSAO[extensao] ?? null;
}

/** Por que um arquivo não serve para a leitura, ou nulo quando serve. */
export function recusaDaLeitura(nome: string, tipoInformado: string, tamanho: number): string | null {
  const tipo = tipoDoArquivo(nome, tipoInformado);
  if (!tipo || !TIPOS_DA_LEITURA.has(tipo)) return "A leitura automática aceita PDF e imagem (PNG, JPG, WEBP e HEIC).";
  if (tamanho > LIMITE_DA_LEITURA_BYTES) return "O arquivo passa de 8 MB. Envie um arquivo menor, ou cole o texto do despacho.";
  if (tamanho === 0) return "O arquivo está vazio.";
  return null;
}

/** Por que um arquivo não pode ser guardado na perícia, ou nulo quando pode. */
export function recusaDoDocumento(nome: string, tipoInformado: string, tamanho: number): string | null {
  if (!tipoDoArquivo(nome, tipoInformado)) return "Os documentos aceitam PDF, imagem (PNG, JPG, WEBP e HEIC), Word, Excel, TXT e CSV.";
  if (tamanho > LIMITE_DOCUMENTO_BYTES) return "O arquivo passa de 20 MB.";
  if (tamanho === 0) return "O arquivo está vazio.";
  return null;
}

/** "26 bytes", "640 KB", "3,2 MB" — em português, com vírgula. Lá o arquivo de 26 bytes dizia "0 KB". */
export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return bytes === 1 ? "1 byte" : `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}
